import api from './api';

// 访客控制配置接口
export interface VisitorControlConfig {
  // 顶级开关
  duplicate_visitor_enabled?: boolean;
  access_limit_enabled?: boolean;
  access_limit_period?: 'hour' | 'day' | 'week' | 'month';
  
  // 具体配置
  access_limit_config?: {
    limit_by_ip?: boolean;
  };
  duplicate_visitor_config?: {
    max_duplicate_count?: number;
  };
}

export interface Link {
  id: number;
  user_id?: number;
  client_id?: number;
  title: string;
  description: string;
  short_code: string;
  default_url: string;
  intercept_url: string;
  status: 'audit' | 'green' | 'block';
  
  // 域名和部署方式
  domain_id?: number;
  domain_name?: string; // 域名名称（用于生成访问链接）
  deploy_type?: 'api' | 'short_link';
  
  // 规则相关
  rule_id?: number;
  rule_name?: string;
  
  // 访客控制配置
  visitor_control_config?: VisitorControlConfig;
  
  // PHP跳转模式配置
  jump_mode?: number;         // 1-跳转URL, 2-显示本地HTML文件
  jump_block_mode?: number;   // 1-跳转URL, 2-显示本地HTML文件, 3-404错误
  
  // 广告平台
  ad_plat?: string;           // 投放广告平台（facebook/google/tiktok/other）
  
  rules_count: number;
  total_visits: number;
  default_redirects: number;
  intercept_redirects: number;
  success_rate: number;
  created_at: string;
  updated_at: string;
}

export interface CreateLinkRequest {
  title: string;
  description?: string;
  short_code?: string; // 可选，如果为空则由后端自动生成包含clientid的短码
  default_url: string;
  intercept_url: string;
  status?: 'audit' | 'green' | 'block';
  
  // 域名和部署方式
  domain_id?: number;
  deploy_type?: 'api' | 'short_link';
  
  // 规则选择
  rule_id?: number;
  
  // 首条规则配置（admin需要）
  rule_config?: any;
  
  // 访客控制配置
  visitor_control_config?: VisitorControlConfig;
  
  // PHP跳转模式配置
  jump_mode?: number;         // 1-跳转URL, 2-显示本地HTML文件
  jump_block_mode?: number;   // 1-跳转URL, 2-显示本地HTML文件, 3-404错误
  
  // 广告平台
  ad_plat?: string;           // 投放广告平台（facebook/google/tiktok/other）
}

export interface UpdateLinkRequest {
  id: number;
  title?: string;
  description?: string;
  short_code?: string;
  default_url?: string;
  intercept_url?: string;
  status?: 'audit' | 'green' | 'block';
  
  // 域名和部署方式
  domain_id?: number;
  deploy_type?: 'api' | 'short_link';
  
  // 规则选择
  rule_id?: number;
  
  // 可选：更新访客控制配置
  visitor_control_config?: VisitorControlConfig;
  
  // PHP跳转模式配置
  jump_mode?: number;         // 1-跳转URL, 2-显示本地HTML文件
  jump_block_mode?: number;   // 1-跳转URL, 2-显示本地HTML文件, 3-404错误
  
  // 广告平台
  ad_plat?: string;           // 投放广告平台（facebook/google/tiktok/other）
}

export interface LinkListResponse {
  list: Link[];
  total?: number;
  page?: number;
  size?: number;
  next_last_id?: number;
  has_more?: boolean;
}

export interface LinkDetailResponse {
  data: Link;
}

// 链接管理API服务
export const linkService = {
  // 获取链接列表
  getLinks: async (params?: any): Promise<LinkListResponse> => {
    const payload = {
      page: 1,
      size: 20,
      last_id: 0,
      without_stats: true,
      ...(params || {}),
    };
    const response = await api.post('/api/links/list', payload);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 创建链接
  createLink: async (data: CreateLinkRequest): Promise<Link> => {
    const response = await api.post('/api/links/create', data);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 更新链接
  updateLink: async (data: UpdateLinkRequest): Promise<Link> => {
    const response = await api.post('/api/links/update', data);
    return response.data.data; // 修复：正确访问 data.data
  },

  // 删除链接
  deleteLink: async (id: number): Promise<void> => {
    await api.post('/api/links/delete', { id });
  },

  // 获取链接详情
  getLinkDetail: async (id: number): Promise<Link> => {
    const response = await api.post('/api/links/detail', { id });
    return response.data.data; // 修复：正确访问 data.data
  },

  // 切换链接状态（运行模式）
  toggleLinkStatus: async (id: number, status: 'audit' | 'green' | 'block'): Promise<void> => {
    await api.post('/api/links/update', { id, status });
  },

  // 切换链接状态（三态循环）
  toggleLink: async (id: number): Promise<{ status: string }> => {
    const response = await api.post('/api/links/toggle', { id });
    return response.data.data;
  },

  // 复制链接到剪贴板
  copyToClipboard: async (text: string): Promise<void> => {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
    } else {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  },

  // 生成PHP文件
  generatePHPFile: async (linkId: number, deployType: string = 'standalone') => {
    const response = await api.post('/api/links/generate-php', { link_id: linkId, deploy_type: deployType });
    return response.data;
  },
}; 